import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { PresentationChart, Gavel, CalendarBlank } from "@phosphor-icons/react";

const dmy = (d) => (d ? d.slice(0, 10).split("-").reverse().join(".") : "—");
const STATUS = { planned: "Geplant", draft: "Entwurf", invited: "Eingeladen", held: "Durchgeführt", closed: "Abgeschlossen" };

function Assemblies() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/weg/hub/all-assemblies").then(({ data }) => setRows(data || [])).catch(() => setRows([])); }, []);
  if (!rows) return <div className="text-sm text-slate-500">Lade …</div>;
  if (!rows.length) return <div className="text-sm text-slate-500" data-testid="weg-hub-assemblies-empty">Noch keine Eigentümerversammlungen. Versammlungen legen Sie im Objekt unter „WEG“ an.</div>;
  return (
    <div className="divide-y border rounded-md bg-white" data-testid="weg-hub-assemblies">
      {rows.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-3 px-3 py-2" data-testid={`weg-hub-assembly-${a.id}`}>
          <CalendarBlank size={16} className="text-slate-400" />
          <div className="min-w-0 flex-1">
            <Link to={`/weg/versammlung/${a.id}`} className="font-medium text-slate-900 hover:underline">{a.title || "Eigentümerversammlung"}</Link>
            <div className="text-xs text-slate-500">{a.property_name} · {dmy(a.assembly_date)} · {a.agenda_count} TOPs · {STATUS[a.status] || a.status}</div>
          </div>
          <Button size="sm" variant="outline" asChild data-testid={`weg-hub-present-${a.id}`}>
            <Link to={`/weg/versammlung/${a.id}/praesentation`}><PresentationChart size={14} className="mr-1" /> Präsentation</Link>
          </Button>
        </div>
      ))}
    </div>
  );
}

function Resolutions() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  useEffect(() => {
    const t = setTimeout(() => api.get("/weg/hub/all-resolutions", { params: q ? { q } : {} }).then(({ data }) => setRows(data || [])).catch(() => setRows([])), 250);
    return () => clearTimeout(t);
  }, [q]);
  return (
    <div className="space-y-2" data-testid="weg-hub-resolutions">
      <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Beschlüsse durchsuchen (Titel, Text)…" className="max-w-md" data-testid="weg-hub-resolutions-search" />
      <div className="border rounded-md bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs text-slate-600 text-left"><tr><th className="px-3 py-2">Nr.</th><th className="px-3 py-2">Datum</th><th className="px-3 py-2">Objekt</th><th className="px-3 py-2">Beschluss</th><th className="px-3 py-2">Ergebnis</th><th className="px-3 py-2">Ja/Nein/Enth.</th></tr></thead>
          <tbody className="divide-y">
            {rows.map((r) => (
              <tr key={r.id} data-testid={`weg-hub-resolution-${r.id}`} className={r.invalid ? "opacity-60" : ""}>
                <td className="px-3 py-2 font-mono">{r.resolution_number}</td>
                <td className="px-3 py-2">{dmy(r.resolution_date)}</td>
                <td className="px-3 py-2">{r.property_name}</td>
                <td className="px-3 py-2"><div className="font-medium">{r.title}</div>{r.text && <div className="text-xs text-slate-500 line-clamp-2">{r.text}</div>}</td>
                <td className="px-3 py-2">{r.outcome}{r.challenge_status && r.challenge_status !== "keine" ? ` · angefochten` : ""}</td>
                <td className="px-3 py-2 tabular-nums">{r.votes_yes ?? 0}/{r.votes_no ?? 0}/{r.votes_abstain ?? 0}</td>
              </tr>
            ))}
            {!rows.length && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-500">Keine Beschlüsse gefunden.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function WegHubTabs() {
  const [tab, setTab] = useState("assemblies");
  const T = ({ k, icon: I, label }) => (
    <button onClick={() => setTab(k)} className={`px-3 py-1.5 text-sm rounded-md flex items-center gap-1.5 ${tab === k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"}`} data-testid={`weg-hub-tab-${k}`}><I size={14} /> {label}</button>
  );
  return (
    <section className="space-y-3">
      <div className="flex gap-2"><T k="assemblies" icon={PresentationChart} label="Eigentümerversammlungen" /><T k="resolutions" icon={Gavel} label="Beschlusssammlung" /></div>
      {tab === "assemblies" ? <Assemblies /> : <Resolutions />}
    </section>
  );
}
